import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import type {
  CrmCreateLeadFunnelTaskRequest,
  CrmLeadTask,
  CrmLeadTasksPayload,
  CrmLeadTasksStatus,
} from '@/api/types/crm/crmLeadTasks.types'
import { useLeadRefetch } from '../../LeadRefetchContext'
import { CRM_LEAD_TASKS_DEFAULT_PER_PAGE } from './leadTasks.utils'

type UseLeadTasksParams = {
  leadId: number | null | undefined
  funnelId: number | null | undefined
  leadFunnelId: number | null | undefined
  enabled?: boolean
}

export function useLeadTasks({
  leadId,
  funnelId,
  leadFunnelId,
  enabled = true,
}: UseLeadTasksParams) {
  const addAlert = useAddAlert()
  const addAlertRef = useRef(addAlert)
  addAlertRef.current = addAlert
  const { putCardInFunnelSnapshot } = useLeadRefetch()
  const putCardInFunnelSnapshotRef = useRef(putCardInFunnelSnapshot)
  putCardInFunnelSnapshotRef.current = putCardInFunnelSnapshot

  const [items, setItems] = useState<CrmLeadTask[]>([])
  const [counts, setCounts] = useState<{ today: number; overdue: number }>({
    today: 0,
    overdue: 0,
  })
  const [totalCount, setTotalCount] = useState(0)
  const [status, setStatusState] = useState<CrmLeadTasksStatus>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(CRM_LEAD_TASKS_DEFAULT_PER_PAGE)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  const totalPages = useMemo(() => {
    if (perPage <= 0 || totalCount <= 0) return 1
    return Math.max(1, Math.ceil(totalCount / perPage))
  }, [perPage, totalCount])

  const setStatus = useCallback((next: CrmLeadTasksStatus) => {
    setStatusState(next)
    setCurrentPage(1)
  }, [])

  const setPerPageAndResetPage = useCallback((value: number) => {
    setPerPage(value)
    setCurrentPage(1)
  }, [])

  const nextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
  }, [totalPages])

  const prevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1))
  }, [])

  // Смена сделки / воронки — с первой страницы.
  useEffect(() => {
    setCurrentPage(1)
  }, [leadFunnelId, funnelId, leadId])

  // Если total уменьшился (удаление) — не оставлять пустую страницу.
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const load = useCallback(
    async (options?: { silent?: boolean; page?: number; pageSize?: number }) => {
      const lfId = Number(leadFunnelId)
      const lid = Number(leadId)
      const fid = Number(funnelId)
      const canLoad = enabled && Number.isFinite(lfId) && lfId > 0
      const canUseCardFunnel =
        canLoad &&
        Number.isFinite(lid) &&
        lid > 0 &&
        Number.isFinite(fid) &&
        fid > 0

      if (!canLoad) {
        setItems([])
        setCounts({ today: 0, overdue: 0 })
        setTotalCount(0)
        return
      }

      const silent = Boolean(options?.silent)
      const page = options?.page ?? currentPage
      const pageSize = options?.pageSize ?? perPage
      const cursor = Math.max(0, (page - 1) * pageSize)

      if (!silent) setLoading(true)

      try {
        let payload: CrmLeadTasksPayload | null | undefined

        if (canUseCardFunnel) {
          const res = await crmApi.leads.getLeadCardInFunnel({
            lead_id: lid,
            funnel_id: fid,
            lead_funnel_id: lfId,
            tasks_status: status,
            tasks_limit: pageSize,
            tasks_cursor: cursor,
          })
          if (isError(res)) throw new Error('card-funnel tasks failed')
          const cardData = res.data?.data
          if (cardData) {
            putCardInFunnelSnapshotRef.current(fid, {
              ...cardData,
              lead_funnel_id:
                Number(cardData.lead_funnel_id) > 0
                  ? Number(cardData.lead_funnel_id)
                  : lfId,
            })
          }
          payload = cardData?.tasks
        } else {
          const res = await crmApi.leads.getLeadFunnelTasks({
            leadFunnelId: lfId,
            status,
            limit: pageSize,
            cursor,
          })
          if (isError(res)) throw new Error('reference-items failed')
          payload = res.data?.data
        }

        const nextItems =
          payload && Array.isArray(payload.items) ? payload.items : []
        setItems(nextItems)
        // counts — бейджи; бэк не завязывает их на tasks_status.
        setCounts({
          today: Number(payload?.counts?.today) || 0,
          overdue: Number(payload?.counts?.overdue) || 0,
        })
        setTotalCount(Number(payload?.total_count) || 0)
      } catch {
        if (!silent) {
          setItems([])
          setCounts({ today: 0, overdue: 0 })
          setTotalCount(0)
          addAlertRef.current({
            title: 'Ошибка',
            text: 'Не удалось загрузить задачи',
            type: 'error',
          })
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [
      currentPage,
      enabled,
      funnelId,
      leadFunnelId,
      leadId,
      perPage,
      status,
    ],
  )

  useEffect(() => {
    void load()
  }, [load])

  const createTask = useCallback(
    async (body: CrmCreateLeadFunnelTaskRequest) => {
      setCreating(true)
      try {
        const res = await crmApi.leads.createLeadFunnelTask(body)
        if (isError(res)) throw new Error()
        const created = res.data?.data
        setCurrentPage(1)
        await load({ silent: true, page: 1 })
        addAlertRef.current({
          title: 'Успешно',
          text: 'Задача создана',
          type: 'success',
        })
        return created
      } catch {
        addAlertRef.current({
          title: 'Ошибка',
          text: 'Не удалось создать задачу',
          type: 'error',
        })
        return undefined
      } finally {
        setCreating(false)
      }
    },
    [load],
  )

  return {
    items,
    counts,
    totalCount,
    loading,
    creating,
    reload: load,
    reloadSilent: () => load({ silent: true }),
    createTask,
    status,
    setStatus,
    currentPage,
    perPage,
    totalPages,
    setCurrentPage,
    setPerPage: setPerPageAndResetPage,
    nextPage,
    prevPage,
  }
}
